// Remembered-account chip for the login experiment (z94kydcrz3).
//
// Stores the email of the last account that logged in on this device, and
// nothing else: no name (the sign-in API does not return one), no token, no
// id, no workspace or client data (the PRD forbids anything account-specific
// before authentication). Every failure (blocked storage, a private window, a
// corrupt or oversized value) reads as "nothing remembered", and the page
// falls back to the full form.
//
// Storage access is passed in so this stays testable outside a browser.

const REMEMBERED_ACCOUNT_KEY = "sc_remembered_account";
const MAX_EMAIL = 254;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const cleanEmail = (value) =>
  typeof value === "string" ? value.trim().toLowerCase() : "";

const isValidEmail = (email) =>
  !!email && email.length <= MAX_EMAIL && EMAIL_PATTERN.test(email);

function readRememberedAccount(getItem) {
  let raw;
  try {
    raw = getItem(REMEMBERED_ACCOUNT_KEY);
  } catch (e) {
    return null;
  }
  if (typeof raw !== "string" || !raw || raw.length > 1024) return null;
  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch (e) {
    return null;
  }
  if (!parsed || typeof parsed !== "object") return null;
  const email = cleanEmail(parsed.email);
  return isValidEmail(email) ? { email } : null;
}

/** Remember this account's email after a successful login. */
function writeRememberedAccount(setItem, account) {
  const email = cleanEmail(account && account.email);
  if (!isValidEmail(email)) return false;
  try {
    return setItem(REMEMBERED_ACCOUNT_KEY, JSON.stringify({ email })) !== false;
  } catch (e) {
    return false;
  }
}

function clearRememberedAccount(removeItem) {
  try {
    removeItem(REMEMBERED_ACCOUNT_KEY);
  } catch (e) {
    // Nothing to clear when storage is blocked.
  }
}

/** Up to two initials for the avatar, from the email's local part. */
function accountInitials(account) {
  const local = ((account && account.email) || "").split("@")[0];
  const letters = local
    .split(/[\s._+-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]);
  return (letters.join("") || "?").toUpperCase();
}

module.exports = {
  REMEMBERED_ACCOUNT_KEY,
  accountInitials,
  clearRememberedAccount,
  readRememberedAccount,
  writeRememberedAccount,
};
